// packages import
import { Autocomplete, TextField } from '@mui/material'
import { FC } from 'react'
import { SelectionField } from '../../../../generated'
import { capitalizeCase } from '../../../utils'

interface EnumFilterProps {
  field: SelectionField
  selectedOption: any
  setReportFilters: any
}

// Display-only label overrides, keyed by field name then option value. These change the
// dropdown text ONLY — the value sent to the API is untouched, so 'draft' still maps to
// the draft status in the query.
//
// Bills by Matter is the only report whose status dropdown exposes a `draft` option, and
// a draft bill there is a pre-bill, which the bare status name doesn't convey.
const OPTION_LABEL_OVERRIDES: Record<string, Record<string, string>> = {
  status: { draft: 'Draft (Pre-Bills)' }
}

const EnumFilter: FC<EnumFilterProps> = ({ field, selectedOption, setReportFilters }) => {
  const { options, label, field: fieldName } = field
  const enumOptions = options?.map((option: string) => ({
    value: option,
    label: OPTION_LABEL_OVERRIDES[fieldName]?.[option] ?? capitalizeCase(option || '')
  }))
  // Prefer the user's selection; fall back to defaultValue only when nothing is
  // selected yet. (Using an OR here pins the dropdown to defaultValue forever,
  // since the default option always matches and `find` returns it first.)
  const effectiveValue = selectedOption ?? field.defaultValue
  const value = enumOptions?.find((option: any) => option.value === effectiveValue) ?? null

  const handleStatusChange = (value: any | null) => {
    setReportFilters((prev: any) => ({ ...prev, [fieldName]: value?.value }))
  }

  return (
    <Autocomplete
      sx={{ minWidth: 250 }}
      options={enumOptions || []}
      value={value}
      onChange={(_, value) => handleStatusChange(value)}
      getOptionLabel={option => option.label}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      renderInput={params => <TextField {...params} label={label} />}
    />
  )
}

export default EnumFilter
